import React, { useEffect, useRef, useState } from 'react';
import { X, RotateCcw, Trash2, Share2, Check, Copy } from 'lucide-react';
import WinnerCelebration from './brand/WinnerCelebration';
import { shareWinner, tweetUrl, type WinnerCardCopy } from '../utils/winnerCard';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useLang } from '../utils/i18n';
// Confetti is decoration, not selection — but routing it through the same
// helper keeps `Math.random` out of every user-facing file, so the grep in
// TASK-01 stays meaningful instead of returning known-benign noise.
import { randomFloat } from '../utils/random';

interface WinnerModalProps {
  winner: string;
  /** Optional uploaded image attached to the selected entry. */
  winnerImage?: string;
  /** Named on the shared card, so a result says which draw it came from. */
  wheelTitle: string;
  showConfetti?: boolean;
  onClose: () => void;
  onRemoveAndSpin: () => void;
  onSpinAgain: () => void;
}

const CONFETTI_COLORS = [
  '#EE4040', '#F0A30A', '#7DBD43', '#3BAED0',
  '#9B59B6', '#E67E22', '#27AE60', '#E91E8C',
  '#2980B9', '#FF6B6B', '#1ABC9C', '#F39C12',
];

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  size: number;
  delay: number;
  duration: number;
  shape: 'rect' | 'circle';
  rotation: number;
}

const Confetti: React.FC = () => {
  const pieces = useRef<ConfettiPiece[]>(
    Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: randomFloat() * 100,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      size: randomFloat() * 8 + 5,
      delay: randomFloat() * 1.2,
      duration: 2.5 + randomFloat() * 2,
      shape: randomFloat() > 0.4 ? 'rect' : 'circle',
      rotation: randomFloat() * 360,
    }))
  ).current;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {pieces.map((p) => (
        <div
          key={p.id}
          className="absolute confetti-piece"
          style={{
            left: `${p.x}%`,
            top: '-20px',
            width: p.shape === 'circle' ? p.size : p.size * 0.6,
            height: p.shape === 'circle' ? p.size : p.size * 1.4,
            backgroundColor: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : '2px',
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            transform: `rotate(${p.rotation}deg)`,
            opacity: 0,
          }}
        />
      ))}
    </div>
  );
};

const WinnerModal: React.FC<WinnerModalProps> = ({
  winner, winnerImage, wheelTitle,
  showConfetti = true,
  onClose,
  onRemoveAndSpin,
  onSpinAgain,
}) => {
  const { t } = useLang();
  // 'shared' vs 'downloaded' are different outcomes worth reporting differently:
  // one handed the card to the OS share sheet, the other put a PNG in Downloads
  // and the visitor needs to know to go and find it.
  const [shared, setShared] = useState<'shared' | 'downloaded' | null>(null);
  const [copied, setCopied] = useState(false);
  const localizedWinnerText = t('wheel.winnerClipboard', { name: winner });
  const winnerCardCopy: WinnerCardCopy = {
    resultLabel: t('wheel.winner'),
    attribution: 'thenamewheel.com',
    shareText: `${localizedWinnerText} — ${wheelTitle}`,
    shareTitle: localizedWinnerText,
  };

  // WCAG 2.1.2 + 2.4.3 — trap focus inside the modal, restore on close
  const trapRef = useFocusTrap(true);

  // Spin Again / Close / Escape can all unmount this inside the 2.5 s window.
  const shareTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (shareTimer.current) clearTimeout(shareTimer.current);
  }, []);

  const handleShare = async () => {
    const outcome = await shareWinner(winner, wheelTitle, winnerCardCopy);
    setShared(outcome);
    if (shareTimer.current) clearTimeout(shareTimer.current);
    shareTimer.current = setTimeout(() => setShared(null), 2500);
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <>
      {showConfetti && <Confetti />}
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {/* Modal — WCAG dialog pattern */}
        <div
          ref={trapRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="winner-modal-title"
          aria-describedby="winner-modal-desc"
          className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center animate-winner-pop"
        >
          {/* sr-only description for screen readers */}
          <p id="winner-modal-desc" className="sr-only">
            {t('wheel.winnerDialogDesc')}
          </p>

          {/* Close */}
          <button
            onClick={onClose}
            aria-label={t('wheel.closeWinner')}
            className="absolute top-4 end-4 p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all focus-visible:ring-2 focus-visible:ring-violet-400"
          >
            <X size={18} />
          </button>

          {/* Show the selected image itself when this is an image-backed entry.
              The adjacent heading is its accessible name, so this thumbnail is
              decorative and does not cause the winner to be announced twice. */}
          {winnerImage ? (
            <img
              src={winnerImage}
              alt=""
              className="mx-auto mb-4 h-32 w-32 rounded-2xl object-cover shadow-lg ring-4 ring-violet-100 dark:ring-violet-900/50"
              onError={event => { event.currentTarget.hidden = true; }}
            />
          ) : (
            <WinnerCelebration className="mb-4" />
          )}

          <p id="winner-modal-title" className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">
            {t('wheel.winner')}
          </p>
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white break-words leading-tight mb-8">
            <bdi>{winner}</bdi>
          </h2>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <button
              onClick={onSpinAgain}
              className="flex items-center justify-center gap-2 w-full py-3 bg-violet-600 text-white font-bold rounded-xl hover:bg-violet-700 active:bg-violet-800 transition-colors"
            >
              <RotateCcw size={16} />
              {t('wheel.spinAgain')}
            </button>
            <button
              onClick={onRemoveAndSpin}
              className="flex items-center justify-center gap-2 w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 dark:active:bg-gray-500 transition-colors"
            >
              <Trash2 size={15} />
              {t('wheel.removeAndSpin')}
            </button>
            {/* Share row — flex-wrap so three buttons wrap cleanly on narrow
                viewports (320 px) instead of overflowing. */}
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(t('wheel.winnerClipboard', { name: winner }));
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  } catch { /* clipboard blocked — fail silently */ }
                }}
                className="flex-1 min-w-0 flex items-center justify-center gap-2 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-sm rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {copied ? <Check size={15} className="text-green-500" /> : <Copy size={15} />}
                {copied ? t('wheel.shared') : t('wheel.copyWinner')}
              </button>
              <button
                onClick={handleShare}
                className="flex-1 min-w-0 flex items-center justify-center gap-2 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-sm rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {shared === 'downloaded' ? <Check size={15} className="text-green-500" />
                  : shared === 'shared' ? <Check size={15} className="text-green-500" />
                  : <Share2 size={15} />}
                {shared === 'downloaded' ? t('wheel.imageSaved') : shared === 'shared' ? t('wheel.resultShared') : t('wheel.shareResult')}
              </button>
              <button
                type="button"
                onClick={() => window.open(tweetUrl(winner, wheelTitle, winnerCardCopy), '_blank', 'noopener,noreferrer')}
                title={t('wheel.postOnX')}
                aria-label={t('wheel.postResultOnX')}
                className="flex items-center justify-center px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
                  <path d="M18.9 2H22l-7.4 8.5L23.3 22h-6.8l-5.3-7-6.1 7H2l7.9-9.1L1.7 2h7l4.8 6.4L18.9 2Zm-1.2 18h1.9L7.4 3.9H5.4L17.7 20Z" />
                </svg>
              </button>
            </div>
            <button
              onClick={onClose}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 py-2 transition-colors"
            >
              {t('wheel.close')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export { Confetti };
export default WinnerModal;


